// ── Services data store ───────────────────────────────────────────────────────
// Source priority (highest → lowest):
//   1. Google Sheets cache — if VITE_GOOGLE_SHEET_CSV_URL is set and cache is fresh.
//      Sheet is authoritative for every category it contains.
//   2. Admin localStorage (gfs_admin_services_v4) — manual edits / fallback.
//   3. Hard-coded initial data (src/data/initial-services.ts) — first-run seed.
//
// Supplement rule (sheet mode only):
//   Seed services are appended ONLY for categories not covered by the sheet.
//   Category membership — not service ID — determines suppression.
//   If the sheet has any PS5 services → all seed PS5 services are suppressed.
//   If the sheet has no iPhone services → seed iPhone services are appended.
//   IDs are NOT used for deduplication because sheet uses underscores, seed uses hyphens.

import type { ServiceEntry, DeviceCategory } from '../types/services';
import { INITIAL_SERVICES } from '../data/initial-services';
import { UPSELL_PRODUCTS, UPSELL_ID_PATCHES } from '../data/upsell-products';
import {
  getCachedSheetData,
  isCacheFresh,
  syncServicesFromSheet,
  type SyncResult,
} from './sheets-sync';

// v4 — added Switch 2 seed services; clears stale v3 snapshots that predate them
const STORAGE_KEY = 'gfs_admin_services_v4';

// ── Sheet URL (set VITE_GOOGLE_SHEET_CSV_URL in .env) ─────────────────────────
const SHEET_URL = import.meta.env.VITE_GOOGLE_SHEET_CSV_URL as string | undefined;

// ── Seed fallback ─────────────────────────────────────────────────────────────
// Wrapped in try/catch — Safari Private Browsing throws SecurityError on any localStorage access.
function seed(): ServiceEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { return JSON.parse(stored) as ServiceEntry[]; } catch { /* fall through */ }
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_SERVICES));
  } catch {
    // localStorage unavailable (Safari Private Browsing, storage quota exceeded, etc.)
  }
  return INITIAL_SERVICES;
}

// ── CRUD ──────────────────────────────────────────────────────────────────────

export interface ServiceStats {
  source: 'sheet' | 'seed-only';
  sheetCount: number;
  sheetCategories: DeviceCategory[];
  seedSupplementCount: number;
  seedSupplementCategories: DeviceCategory[];
  seedSkippedCount: number;
  seedSkippedCategories: DeviceCategory[];
  totalActive: number;
}

let _lastStats: ServiceStats | null = null;

/** Returns stats from the most recent getServices() call. Useful for debug panels. */
export function getLastServiceStats(): ServiceStats | null {
  return _lastStats;
}

/**
 * Returns the current service list, with upsell products and upsell-ID patches
 * merged in on top of the base data.
 *
 * Priority for base data: fresh Sheet cache → admin localStorage → initial seed.
 * Upsell products (accessories, add-on services) are always merged from code.
 * Upsell ID patches inject additional upsell relationships onto sheet services.
 */
export function getServices(): ServiceEntry[] {
  // ── 1. Base services (sheet or fallback) ───────────────────────────────────
  let base: ServiceEntry[];
  let stats: ServiceStats;

  if (SHEET_URL) {
    const cached = getCachedSheetData();
    if (cached && isCacheFresh(cached.fetchedAt)) {
      // Sheet is authoritative for every category it contains.
      // Supplement with seed services ONLY for categories not in the sheet.
      // Category membership — not service ID — determines suppression.
      const sheetCategories = new Set(cached.services.map(s => s.deviceCategory));

      const activeSeed = INITIAL_SERVICES.filter(s => s.status === 'active');
      const seedSupplement = activeSeed.filter(s => !sheetCategories.has(s.deviceCategory));
      const seedSkipped    = activeSeed.filter(s =>  sheetCategories.has(s.deviceCategory));

      const supplementCats = [...new Set(seedSupplement.map(s => s.deviceCategory))];
      const skippedCats    = [...new Set(seedSkipped.map(s => s.deviceCategory))];

      base = [...cached.services, ...seedSupplement];

      stats = {
        source: 'sheet',
        sheetCount: cached.services.length,
        sheetCategories: [...sheetCategories] as DeviceCategory[],
        seedSupplementCount: seedSupplement.length,
        seedSupplementCategories: supplementCats,
        seedSkippedCount: seedSkipped.length,
        seedSkippedCategories: skippedCats,
        totalActive: base.filter(s => s.status === 'active').length,
      };

      console.info(
        `[GameFix Store] sheet=${stats.sheetCount} (${stats.sheetCategories.join(', ')})` +
        ` | supplement=${stats.seedSupplementCount} (${stats.seedSupplementCategories.join(', ')})` +
        ` | suppressed=${stats.seedSkippedCount} (${stats.seedSkippedCategories.join(', ')})` +
        ` | total active=${stats.totalActive}`
      );
    } else {
      base = seed();
      stats = _seedOnlyStats(base);
    }
  } else {
    base = seed();
    stats = _seedOnlyStats(base);
  }

  _lastStats = stats;

  // ── 2. Merge upsell products (skip any already in base by id) ─────────────
  const baseIds = new Set(base.map(s => s.id));
  const newUpsells = UPSELL_PRODUCTS.filter(u => !baseIds.has(u.id));
  let services = [...base, ...newUpsells];

  // ── 3. Apply upsell ID patches (additive — never removes existing IDs) ─────
  services = services.map(s => {
    const extra = UPSELL_ID_PATCHES[s.id];
    if (!extra || extra.length === 0) return s;
    const merged = [...new Set([...s.upsellIds, ...extra])];
    return { ...s, upsellIds: merged };
  });

  return services;
}

function _seedOnlyStats(base: ServiceEntry[]): ServiceStats {
  const cats = [...new Set(base.map(s => s.deviceCategory))] as DeviceCategory[];
  return {
    source: 'seed-only',
    sheetCount: 0,
    sheetCategories: [],
    seedSupplementCount: base.filter(s => s.status === 'active').length,
    seedSupplementCategories: cats,
    seedSkippedCount: 0,
    seedSkippedCategories: [],
    totalActive: base.filter(s => s.status === 'active').length,
  };
}

/**
 * Trigger a background sync from Google Sheets.
 * Returns the SyncResult (ok, services, rowCount, error).
 * After a successful sync the next call to getServices() will return fresh data.
 * No-ops and returns a stub result if VITE_GOOGLE_SHEET_CSV_URL is not set.
 */
export async function triggerSheetSync(): Promise<SyncResult & { configured: boolean }> {
  if (!SHEET_URL) {
    return { configured: false, ok: false, services: [], rowCount: 0,
      error: 'VITE_GOOGLE_SHEET_CSV_URL is not set in .env' };
  }
  const result = await syncServicesFromSheet(SHEET_URL);
  return { ...result, configured: true };
}

export function saveServices(services: ServiceEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(services));
  } catch {
    // localStorage unavailable — changes will not persist across page loads
  }
}

export function updateService(updated: ServiceEntry): ServiceEntry[] {
  const services = getServices().map(s =>
    s.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : s
  );
  saveServices(services);
  return services;
}

export function addService(entry: Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'>): ServiceEntry[] {
  const now = new Date().toISOString();
  const newEntry: ServiceEntry = {
    ...entry,
    id: `svc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: now,
    updatedAt: now,
  };
  const services = [...getServices(), newEntry];
  saveServices(services);
  return services;
}

export function deleteService(id: string): ServiceEntry[] {
  const services = getServices().filter(s => s.id !== id);
  saveServices(services);
  return services;
}

// ── Import / Export ───────────────────────────────────────────────────────────
export function exportJSON(): string {
  return JSON.stringify(getServices(), null, 2);
}

export function importJSON(json: string): { ok: boolean; count: number; error?: string } {
  try {
    const parsed = JSON.parse(json) as ServiceEntry[];
    if (!Array.isArray(parsed)) throw new Error('Expected an array');
    saveServices(parsed);
    return { ok: true, count: parsed.length };
  } catch (e) {
    return { ok: false, count: 0, error: String(e) };
  }
}

/** Reset to the hard-coded initial data (destructive) */
export function resetToInitial(): ServiceEntry[] {
  saveServices(INITIAL_SERVICES);
  return INITIAL_SERVICES;
}
